import axios, { type AxiosError, type AxiosResponse } from 'axios'
import { requestFreshTokens, webviewRuntime } from 'modules/shared/page-enhancers/src/utils/webview-runtime'
import {
  appEnv,
  CREDIT_API_ENDPOINT,
  CUSTOMER_API_ENDPOINT,
  CUSTOMER_API_ENDPOINT_V1,
  PORTAL_API_ENDPOINT,
  RAV_API_ENDPOINT
} from 'shared/envs'
import { isomorphicPush } from 'shared/navigation'

const defaultAllowStatus401 = [
  /\/documents\/dirf/i,
  /\/organizations\/payments/i,
  /\/payments\/wallet-types/i,
  /\/users\/current\/stonecodes/i,
  /\/transactions/i,
  /\/acquirer\/receivables/i,
  /\/logr\/report/i,
  /\/flagr\/features\/resource/i,
  /\/resources/i,
  /\/credit-lines/i,
  /\/loans/i,
  /\/authenticate\/validatePassword/i,
  /\/customer\/[0-9]{11,14}\/(agreements|condition|financial-position)$/i,
  /\/(merchants|affiliations)\/[0-9]{9}\/bank-accounts/i,
  /\/identity\/confirm-email/i
]

const homologAllowStatus401 = [
  CUSTOMER_API_ENDPOINT(),
  RAV_API_ENDPOINT(),
  PORTAL_API_ENDPOINT(),
  CUSTOMER_API_ENDPOINT_V1(),
  CREDIT_API_ENDPOINT()
]
  .filter(Boolean)
  .map(apiUrl => new RegExp(String(apiUrl), 'i'))

const bypassLogoutErrorCodes = ['payment_link_interaction_not_found']

function shouldRedirect(url?: string, data?: { error?: string }) {
  if (bypassLogoutErrorCodes.includes(data?.error as string)) {
    return false
  }

  const allowStatus401 = appEnv.isPoitingToHomolog()
    ? [...homologAllowStatus401, ...defaultAllowStatus401]
    : defaultAllowStatus401

  return !allowStatus401.some(apiUrl => apiUrl.test(url as string))
}

export async function allowStatus401Interceptor(error: AxiosError) {
  const { status, config, data } = error?.response || ({} as AxiosResponse)

  if (status !== 401) {
    return Promise.reject(error)
  }

  const isWebView = webviewRuntime.isWebView()

  if (isWebView) {
    try {
      console.info('[401Interceptor] WebView 401 — requesting fresh tokens from native app')
      const freshTokens = await requestFreshTokens()
      console.info('[401Interceptor] Fresh tokens received — retrying original request')

      const retryConfig = { ...config, headers: { ...config.headers, Authorization: `Bearer ${freshTokens.token}` } }
      return axios(retryConfig)
    } catch (retryError) {
      console.error('[401Interceptor] Token refresh failed, cannot retry:', retryError)
      return Promise.reject(error)
    }
  }

  if (shouldRedirect(`${config.baseURL}${config.url}`, data)) {
    isomorphicPush(`/sair?shouldRedirect=true`)
    return Promise.reject({ ...error, isRedirecting: true })
  }

  return Promise.reject(error)
}
